import { useMemo } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type Chart,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

const BRAND = '#8fbd56';
const LIVE = 'red';

// Up red / down blue arrows where |sigmaFactor| crosses the highlight threshold
// (exact port of the legacy sigmaArrowsPlugin / drawArrow geometry).
const sigmaArrows: Plugin<'line'> = {
  id: 'sigmaArrows',
  afterDatasetsDraw(chart: Chart, _a, opts: { highlight?: number | null; sigmaFactors?: (number | null)[] }) {
    const h = opts?.highlight;
    const sf = opts?.sigmaFactors;
    if (h == null || Number.isNaN(h) || h === 0 || !sf) return;
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    meta.data.forEach((pt, i) => {
      const v = sf[i];
      if (!v || v === 0) return;
      const up = h > 0 && v >= h;
      const down = h < 0 && v <= h;
      if (!up && !down) return;
      const { x, y } = pt as unknown as { x: number; y: number };
      const negative = down; // arrow points down
      const yLine = negative ? y + 30 : y - 30;
      const arrowHeadY = negative ? y + 25 : y - 25;
      const startPointY = negative ? y + 50 : y - 50;
      const arrowSize = 12;
      const angle = Math.atan2(yLine - y, 0);
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, startPointY);
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.strokeStyle = negative ? 'blue' : 'red';
      ctx.lineTo(x, yLine);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(x, arrowHeadY);
      ctx.lineTo(x + arrowSize * Math.cos(angle - Math.PI / 5), arrowHeadY + arrowSize * Math.sin(angle - Math.PI / 5));
      ctx.moveTo(x, arrowHeadY);
      ctx.lineTo(x + arrowSize * Math.cos(angle + Math.PI / 5), arrowHeadY + arrowSize * Math.sin(angle + Math.PI / 5));
      ctx.stroke();
      ctx.restore();
    });
  },
};

// Reporting-currency label at the top-left, hidden when plotting the sigma factor
// (chartData === 1) or when no currency is known. Exact port of the legacy
// sigmaCurrencyLabelPlugin.
const sigmaCurrencyLabel: Plugin<'line'> = {
  id: 'sigmaCurrencyLabel',
  afterDraw(chart: Chart, _a, opts: { reportingCurrency?: string; chartData?: number }) {
    const rc = opts?.reportingCurrency;
    if (opts?.chartData === 1 || !rc) return;
    const { ctx, chartArea: area } = chart;
    ctx.save();
    ctx.font = '12px sans-serif';
    ctx.fillStyle = '#666';
    ctx.textAlign = 'right';
    ctx.fillText(rc, area.left, area.top - 20);
    ctx.restore();
  },
};

export function SigmaChart({
  labels,
  values,
  sigmaFactors,
  highlight,
  showDots,
  livePointIndex = -1,
  reportingCurrency = '',
  chartData = 0,
}: {
  labels: string[];
  values: (number | null)[];
  sigmaFactors: (number | null)[];
  highlight: number | null;
  showDots: boolean;
  livePointIndex?: number;
  reportingCurrency?: string;
  chartData?: number;
}) {
  const data = useMemo<ChartData<'line'>>(() => {
    const colors = values.map((_, i) => (i === livePointIndex ? LIVE : BRAND));
    const radii = values.map((_, i) => (i === livePointIndex || showDots ? 3 : 0));
    return {
      labels,
      datasets: [
        {
          data: values,
          borderColor: 'green',
          backgroundColor: BRAND,
          cubicInterpolationMode: 'monotone',
          borderWidth: 1,
          pointBackgroundColor: colors,
          pointRadius: radii,
          spanGaps: true,
          fill: false,
        },
      ],
    };
  }, [labels, values, showDots, livePointIndex]);

  const options = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
        sigmaArrows: { highlight, sigmaFactors },
        sigmaCurrencyLabel: { reportingCurrency, chartData },
      },
      scales: { x: { grid: { display: false } }, y: { grid: { color: 'rgba(0,0,0,0.05)' } } },
    }),
    [highlight, sigmaFactors, reportingCurrency, chartData],
  );

  return (
    <div style={{ height: 320 }}>
      <Line data={data as never} options={options as never} plugins={[sigmaArrows, sigmaCurrencyLabel] as never} />
    </div>
  );
}
